import { useCallback, useEffect, useRef, useState } from 'react';
import { travelGroupApi, type CheckStatus } from '@/lib/api';

export type ShopPhase = 'idle' | 'shopping' | 'done' | 'error';

export interface ShopMeetingState {
  phase: ShopPhase;
  status: CheckStatus | null;
  /** The line to render. Comes from the server while running: "3 of 8 — shopping Darin…". */
  message: string;
  start: () => void;
}

const POLL_MS = 3000;

/**
 * A whole meeting is N trips shopped one after another, so the ceiling is much
 * higher than the single-trip hook's. The 5-12 minute estimate this was first
 * sized against was wrong: measured on 2026-08-17, eight attendees across 24
 * routes took **28 minutes** (17:00:53 → 17:29:16) once retries and the advisor
 * refresh are counted. At 400 polls the hook gave up at 20 minutes, EIGHT
 * minutes before the sweep that found 451 fares finished. 1200 polls = one hour.
 * Hitting the stop is not an error — the sweep continues server-side — so the
 * copy says exactly that instead of implying it failed.
 */
const MAX_POLLS = 1200;

/** Refresh the roster every Nth poll so fares appear as each person finishes. */
const REFETCH_EVERY_POLLS = 3;

/**
 * § G23 — drives "shop fares for everyone" from the meeting surface.
 *
 * Mirrors `useCheckPrices` deliberately (POST → poll → surface progress → refresh)
 * rather than inventing a second flow, because the failure mode being avoided is
 * the same one: a click that produces silence, and an organizer who cannot tell a
 * working scrape from a wedged one.
 */
export function useShopMeeting(groupId: string, onProgress?: () => void): ShopMeetingState {
  const [phase, setPhase] = useState<ShopPhase>('idle');
  const [status, setStatus] = useState<CheckStatus | null>(null);
  const [message, setMessage] = useState('');

  const timerRef = useRef<number | null>(null);
  const pollsRef = useRef(0);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Stop polling if the organizer navigates away mid-sweep.
  useEffect(() => clearTimer, [clearTimer]);

  const poll = useCallback(
    async (jobId: string) => {
      pollsRef.current += 1;
      try {
        const s = await travelGroupApi.shopStatus(groupId, jobId);
        setStatus(s);

        if (s.status === 'running') {
          // The server's own progress line names the person being shopped, which
          // is the fact an organizer actually wants while waiting.
          setMessage(s.message || 'Shopping fares…');
          if (pollsRef.current % REFETCH_EVERY_POLLS === 0) onProgressRef.current?.();
          if (pollsRef.current >= MAX_POLLS) {
            setPhase('done');
            setMessage('Still shopping on the server — the roster will fill in as fares land.');
            onProgressRef.current?.();
            return;
          }
          timerRef.current = window.setTimeout(() => poll(jobId), POLL_MS);
          return;
        }

        if (s.status === 'error') {
          setPhase('error');
          setMessage(s.message || 'Shopping the meeting failed. Please try again.');
          onProgressRef.current?.();
          return;
        }

        setPhase('done');
        setMessage(s.message || 'Done.');
        onProgressRef.current?.();
      } catch {
        // A 404 here means the job expired (15-minute TTL) rather than that the
        // shopping failed, and both leave the organizer needing the same next
        // step: look at the roster, press it again if it is still empty.
        setPhase('error');
        setMessage("Couldn't read the shopping progress. The roster below is still the truth.");
      }
    },
    [groupId]
  );

  const start = useCallback(() => {
    if (phase === 'shopping') return;
    clearTimer();
    pollsRef.current = 0;
    setStatus(null);
    setPhase('shopping');
    setMessage('Starting…');
    travelGroupApi
      .shop(groupId)
      .then((res) => {
        if (!res?.jobId) {
          setPhase('error');
          setMessage("Couldn't start shopping. Please try again.");
          return;
        }
        setMessage(`Shopping ${res.attendees} attendee(s)…`);
        timerRef.current = window.setTimeout(() => poll(res.jobId), POLL_MS);
      })
      .catch((e: any) => {
        setPhase('error');
        // The server refuses with a REASON (nobody has an itinerary yet, no
        // checker running); echoing it beats a generic failure.
        setMessage(e?.response?.data?.error || "Couldn't start shopping. Please try again.");
      });
  }, [phase, groupId, clearTimer, poll]);

  return { phase, status, message, start };
}
